/**
 * Viewer-side URL helpers.
 *
 * The **pure** URL builders — `URL_PATHS`, `urlForBlock`, `urlForCollection`,
 * `urlForGroup`, `urlForUsername`, `urlForMemberOnlyEntity`, the pure
 * `absoluteUrl(path, host?)`, and the `CollectionUrlContext` interface —
 * live in `@shared/types/urls`. Import them directly from there at call
 * sites (per `putty/no-re-export`); this file is just for the viewer-
 * specific wrappers that depend on React Router and the local
 * `collectionStore` / `collectionCollection` caches.
 *
 * What lives here:
 *
 *   1. `absoluteUrl(path)` — smart wrapper around the shared
 *      `absoluteUrl`; falls back to `window.location.origin` on local-dev
 *      / staging so share-sheet links point at the running viewer instead
 *      of production.
 *
 *   2. Parsers (hooks) — `useRouteEntity`, `useActiveCollectionId`. URL →
 *      typed entity discriminated union so components never reach for
 *      `useParams()` directly.
 *
 *   3. Data-aware URL hooks — `useUrlForCollection`,
 *      `useSlugContextForCollection`, `useCollectionUrlBuilder`,
 *      `useUrlForCollectionId`. Close the gap between the pure builders
 *      (which take `{parentSlug}` as input) and the common viewer case
 *      ("I have a CollectionItem; give me the right URL") by reading
 *      parent context from the live store.
 *
 *   4. Slug-resolver hooks — `useFindCollectionBySlug`,
 *      `useFindStandaloneCollectionBySlug`. Reverse direction (URL →
 *      cached collection).
 *
 *   5. Deprecated builder stubs — kept while the in-flight import
 *      migration finishes.
 */
import { useCallback, useMemo } from "react";
import { useLocation, useParams } from "react-router-dom";
import { URL_PATHS, WEB_CLIENT_HOST, urlForCollection, urlForMemberOnlyEntity, } from "@shared/types/urls";
import { isTransientId } from "@/data/assistant-query-store";
import { collectionCollection } from "@/data/collection-collection";
import { collectionStore } from "@/data/collection-store";
// ---------------------------------------------------------------------------
// 1. Absolute URL
// ---------------------------------------------------------------------------
/**
 * Join a path-only URL with the canonical viewer host. Use for share-
 * button / "Copy link" affordances.
 *
 * In local dev / staging the host falls back to `window.location.origin`
 * so the link points at the running viewer instead of production. Server-
 * side renders (which never satisfy `typeof window !== "undefined"`) and
 * the prod / `*.putty.me` hosts all route through `WEB_CLIENT_HOST`.
 */
export function absoluteUrl(path) {
    if (typeof window !== "undefined") {
        const host = window.location.origin;
        if (host.endsWith(".putty.me") || host === WEB_CLIENT_HOST) {
            return `${WEB_CLIENT_HOST}${path}`;
        }
        return `${host}${path}`;
    }
    return `${WEB_CLIENT_HOST}${path}`;
}
/**
 * Read the current route into a typed entity. Use this anywhere you
 * previously reached for `useParams<{ collectionId, groupId, ... }>()`.
 *
 * The hook is memoized over location.pathname + params so consumers can
 * include the return value in dependency arrays without churning every
 * render.
 */
export function useRouteEntity() {
    const location = useLocation();
    const params = useParams();
    const pathname = location.pathname;
    return useMemo(() => {
        if (pathname === URL_PATHS.home)
            return { type: "home" };
        if (pathname === URL_PATHS.collectionsTab)
            return { type: "collectionsTab" };
        if (params.blockSlug !== undefined) {
            return { type: "blockDeeplink", blockSlug: params.blockSlug };
        }
        if (params.standaloneSlug !== undefined) {
            return { type: "standaloneCollection", slug: params.standaloneSlug };
        }
        // /g/:entityId, /collection/:collectionId, /group/:groupId all
        // collapse to one shape — the AppScreen treats them identically.
        const entityIdRaw = params.entityId ?? params.collectionId ?? params.groupId;
        if (entityIdRaw !== undefined) {
            if (isTransientId(entityIdRaw)) {
                return { type: "transientCollection", transientId: entityIdRaw };
            }
            return { type: "entity", entityId: entityIdRaw };
        }
        // Two-segment slug URL — has to be checked before the single-segment
        // catch-all, but React Router's matcher hands us both params at
        // once so the order here doesn't matter, only the presence test.
        if (params.collectionSlug !== undefined && params.name !== undefined) {
            return {
                type: "collectionBySlug",
                name: params.name,
                collectionSlug: params.collectionSlug,
            };
        }
        if (params.name !== undefined) {
            return { type: "name", name: params.name };
        }
        return { type: "unknown" };
    }, [
        pathname,
        params.blockSlug,
        params.standaloneSlug,
        params.entityId,
        params.collectionId,
        params.groupId,
        params.name,
        params.collectionSlug,
    ]);
}
/**
 * Convenience selector for the most-asked question: "what's the
 * currently-active collection ID, if any?". Returns `undefined` for
 * non-entity routes (home, profile, block deeplink, etc.).
 *
 * Resolves all three collection-rendering URL shapes against the live
 * collection cache:
 *
 *   - `/g/{id}` / legacy `/collection/{id}` — direct ID, returned as-is.
 *   - `/c/{slug}`                            — standalone collection,
 *                                              looked up by slug.
 *   - `/{name}/{collectionSlug}`             — group-rooted child
 *                                              collection, looked up by
 *                                              `(parentName, slug)` pair.
 *
 * Slug lookups are synchronous against the local cache and return
 * `undefined` while the cache is cold (anonymous visitors, members
 * landing on a slug URL before the collection has synced). Consumers
 * relying on the ID to *render* that collection's contents should
 * still go through `SlugResolvedCollectionContent` /
 * `SlugResolvedStandaloneCollectionContent` in AppScreen — those drive
 * the network resolver. This hook is for "do I treat the current page
 * as that collection?" affordances (lip suppression, the omnibar's
 * destination chip), which can safely no-op until the cache lands.
 */
export function useActiveCollectionId() {
    const entity = useRouteEntity();
    // Slug-route cache lookups have to be called unconditionally for the
    // Rules of Hooks, then only the matching branch consumes the result.
    // `useFindStandaloneCollectionBySlug` / `useFindCollectionBySlug`
    // both short-circuit on undefined inputs, so the non-matching
    // branches pay only the (shared) collection-cache subscription.
    const standaloneCached = useFindStandaloneCollectionBySlug(entity.type === "standaloneCollection" ? entity.slug : undefined);
    const slugChildCached = useFindCollectionBySlug(entity.type === "collectionBySlug" ? entity.name : undefined, entity.type === "collectionBySlug" ? entity.collectionSlug : undefined);
    if (entity.type === "entity")
        return entity.entityId;
    if (entity.type === "standaloneCollection") {
        return standaloneCached?.id;
    }
    if (entity.type === "collectionBySlug") {
        return slugChildCached?.id;
    }
    return undefined;
}
// ---------------------------------------------------------------------------
// 3. Data-aware hooks — wire builders to live store data
// ---------------------------------------------------------------------------
//
// `urlForCollection` is a pure function — it takes whatever context the
// caller can give it. In practice almost every call site has the same
// shape: "I have a CollectionItem; give me the right URL." The hooks
// below close that gap by looking up the parent's slug from the live
// store. Standalone collections need no context at all — their slug is
// globally unique on its own.
/**
 * Resolve the slug-URL context for a single collection from the live
 * store. The only context `urlForCollection` consumes is the parent
 * group's slug (when the collection is group-rooted); standalone
 * collections need none.
 *
 * Pure helper consumed by `useUrlForCollection`,
 * `useSlugContextForCollection`, and `useCollectionUrlBuilder`. Kept out
 * of `urlForCollection` itself so that one stays a dumb pure function
 * suitable for storybook / tests.
 */
function buildSlugContext(parent) {
    const parentSlug = parent?.layerIndex === 0 && parent.isImplicit !== true ? parent.slug : undefined;
    return { parentSlug };
}
/**
 * The "do the right thing" URL for a collection. Live-binds to the
 * store via hooks — every render returns the most current URL for the
 * collection, so consumers can pass the result straight through to
 * `<Link to={...}>` or `<a href={...}>`.
 *
 * Returns `undefined` when `collection` itself is `undefined` so call
 * sites can pass live-cache reads straight through without a
 * conditional.
 *
 * For event-handler usage (where calling hooks per-collection isn't
 * possible), see `useCollectionUrlBuilder` — it returns a stable
 * callback that does the same slug context lookup synchronously.
 */
export function useUrlForCollection(collection) {
    const ctx = useSlugContextForCollection(collection);
    if (collection === undefined)
        return undefined;
    return urlForCollection(collection, ctx);
}
/**
 * Live-bound slug-URL context for a collection. Surfaces the
 * `parentSlug` pair that `useUrlForCollection` uses internally, so
 * callers that need to *preview* what a URL would look like under a
 * different slug (e.g. the collection-settings modal's slug editor)
 * can plug it straight into `urlForCollection({ ..., slug })`.
 *
 * Returns an empty context (`{}`) when `collection` is `undefined`;
 * `urlForCollection` falls through to the `/g/{id}` form in that case.
 */
export function useSlugContextForCollection(collection) {
    const parent = collectionStore.useFindById(collection?.parentCollectionId);
    if (collection === undefined)
        return {};
    return buildSlugContext(parent);
}
/**
 * Callback-style smart URL builder. Returns a stable `(c) => string`
 * function suitable for use inside `useCallback`-wrapped event handlers
 * (where calling `useUrlForCollection` per-row would violate the Rules
 * of Hooks).
 *
 * The returned function does the parent lookup synchronously via the
 * underlying TanStack DB collection (`collectionCollection.get`)
 * instead of `collectionStore.useFindById`. On a cold cache miss the
 * builder falls through to the member-only `/g/{id}` URL — the same
 * fallback `urlForCollection` would have produced.
 */
export function useCollectionUrlBuilder() {
    return useCallback((collection) => {
        const parent = collection.parentCollectionId !== undefined
            ? collectionCollection.get(collection.parentCollectionId)
            : undefined;
        return urlForCollection(collection, buildSlugContext(parent));
    }, []);
}
/**
 * Callback-style URL builder keyed by collection ID. Looks the
 * collection up synchronously from `collectionCollection` and hands it
 * to `useCollectionUrlBuilder`. When the ID isn't in cache (cold-start,
 * tab open for a just-created collection that hasn't synced yet, etc.)
 * the builder falls through to the member-only `/g/{id}` URL, so
 * call-sites can use this without guarding for cache misses.
 */
export function useUrlForCollectionId() {
    const buildUrlForCollection = useCollectionUrlBuilder();
    return useCallback((collectionId) => {
        const collection = collectionCollection.get(collectionId);
        if (collection === undefined) {
            return urlForMemberOnlyEntity(collectionId);
        }
        return buildUrlForCollection(collection);
    }, [buildUrlForCollection]);
}
/**
 * Inverse of `urlForCollection` for the two-segment URL shape: take a
 * `(parentName, collectionSlug)` pair from a URL like `/{name}/{slug}`
 * and try to find the matching group-rooted collection in the live
 * local cache. Used by the slug-route content branch to skip the
 * public-API resolver round-trip when the user is already a member
 * of the collection (the common case for internally-navigated slug
 * URLs — when AppScreen auto-upgrades a `/g/{id}` URL to its slug
 * form, the collection is by definition already in the local cache).
 *
 * Only handles the group-rooted case — standalone collections live at
 * `/c/{slug}` and have their own cache lookup (`useFindStandaloneCollectionBySlug`).
 *
 * Returns `undefined` for slug URLs we don't recognize locally —
 * callers should fall back to `resolveCollection` from the public API
 * in that case.
 */
export function useFindCollectionBySlug(parentName, collectionSlug) {
    const { collections } = collectionStore.useListAllForSlugLookup();
    return useMemo(() => {
        if (parentName === undefined || collectionSlug === undefined)
            return undefined;
        for (const c of collections) {
            if (c.slug !== collectionSlug)
                continue;
            const parent = c.parentCollectionId !== undefined
                ? collections.find((p) => p.id === c.parentCollectionId)
                : undefined;
            if (parent?.layerIndex === 0 &&
                parent.isImplicit !== true &&
                parent.slug === parentName) {
                return c;
            }
        }
        return undefined;
    }, [collections, parentName, collectionSlug]);
}
/**
 * Inverse of `urlForCollection` for the `/c/{slug}` URL shape: take a
 * standalone collection's globally-unique slug and find the matching
 * row in the live local cache. The slug already carries enough
 * disambiguation on its own (see `generateStandaloneCollectionSlug` in
 * `@shared/types/slug`), so no parent / owner context is needed —
 * any layer-1 row with `parentCollectionId === undefined` and a
 * matching `slug` wins.
 *
 * Returns `undefined` for slug URLs we don't recognize locally;
 * callers should fall back to `resolveStandaloneCollection` from the
 * public API in that case.
 */
export function useFindStandaloneCollectionBySlug(slug) {
    const { collections } = collectionStore.useListAllForSlugLookup();
    return useMemo(() => {
        if (slug === undefined)
            return undefined;
        for (const c of collections) {
            if (c.slug === slug &&
                c.parentCollectionId === undefined &&
                (c.layerIndex ?? 1) === 1) {
                return c;
            }
        }
        return undefined;
    }, [collections, slug]);
}
// ---------------------------------------------------------------------------
// 4. Deprecated helpers — kept as named re-exports during the migration
// ---------------------------------------------------------------------------
//
// These were the original helpers from the first URL-rename pass. New
// code should use `urlForCollection` / `urlForGroup` / `urlForBlock`
// (from `@shared/types/urls`) directly; these stubs exist so the in-
// flight migration doesn't break any imports that landed in the
// meantime. Remove once `phase5-migrate-navigate` completes.
/** @deprecated Use `urlForBlock({ slug })` from `@shared/types/urls`. */
export function buildBlockUrl(blockSlug) {
    return `/b/${blockSlug}`;
}
/** @deprecated Use `urlForMemberOnlyEntity(id)` from `@shared/types/urls`. */
export function buildMemberOnlyGroupUrl(shortId) {
    return `/g/${shortId}`;
}
/** @deprecated Use `urlForUsername(name)` or `urlForGroup({ slug })` directly. */
export function buildProfileOrGroupUrl(name) {
    return `/${name}`;
}
